import {
  OpenAPIRegistry,
  OpenApiGeneratorV31,
  type RouteConfig,
} from "@asteasolutions/zod-to-openapi";
import { HealthStatus } from "./common/health.js";
import { Problem } from "./common/problem.js";

// The single OpenAPI registry. Every module contributes its schemas and routes
// here as it lands (catalogue, planning, …) — the generated document IS the
// product contract (doctrine 3): web, floor PWA, admin console, agents, and
// integrations are all generated-client consumers of it.

export const registry = new OpenAPIRegistry();

registry.register("Problem", Problem);
registry.register("HealthStatus", HealthStatus);

export const sessionCookieAuth = registry.registerComponent("securitySchemes", "sessionCookie", {
  type: "apiKey",
  in: "cookie",
  name: "__Secure-assessly_sid",
  description:
    "Opaque DB-backed session cookie (spec 16 §3.1; D-020 — sessions, never JWTs). Floor devices additionally present their device token per spec 18 §3.2.",
});

export const idempotencyKeyParam = registry.registerComponent("parameters", "IdempotencyKey", {
  name: "Idempotency-Key",
  in: "header",
  required: true,
  schema: { type: "string", minLength: 1, maxLength: 200 },
  description:
    "Client-generated key; identical replays return the stored response (TESTING §4.3). Required on every mutation except the /v1/auth/* group (spec 16 §5).",
});

export function defaultErrorResponses(): RouteConfig["responses"] {
  return {
    400: problemResponse("Validation failed"),
    401: problemResponse("Not authenticated"),
    403: problemResponse("Not permitted"),
    404: problemResponse("Not found (also returned where tenancy hides existence)"),
    409: problemResponse("Conflict"),
    422: problemResponse("Semantically invalid"),
  };
}

export function problemResponse(description: string): {
  description: string;
  content: { "application/problem+json": { schema: typeof Problem } };
} {
  return {
    description,
    content: { "application/problem+json": { schema: Problem } },
  };
}

/**
 * The 400 every `/v1` route can answer. The query contract (D-142) refuses a
 * query key the route does not declare, after the guards and before the
 * handler, on every route. A route with its own validation 400 states it there;
 * this is the declaration for a route whose only 400 is that refusal (D-339
 * ruling 47).
 */
export const undeclaredQueryRefused = problemResponse(
  "VALIDATION_FAILED: a query parameter this route does not declare (D-142)",
);

registry.registerPath({
  method: "get",
  path: "/v1/healthz",
  operationId: "getHealth",
  tags: ["platform"],
  summary: "API liveness/readiness probe (ALB target checks)",
  responses: {
    200: {
      description: "API is serving",
      content: { "application/json": { schema: HealthStatus } },
    },
    400: undeclaredQueryRefused,
    503: problemResponse("Dependencies unavailable (degraded/refusing)"),
  },
});

export function generateOpenApiDocument(): ReturnType<OpenApiGeneratorV31["generateDocument"]> {
  const generator = new OpenApiGeneratorV31(registry.definitions);
  return generator.generateDocument({
    openapi: "3.1.0",
    info: {
      title: "Assessly Factory OS API",
      version: "1.0.0",
      description:
        "The versioned contract every client consumes — office SPA, floor PWA, platform console, AI agents, and tenant integrations. Generated from packages/contracts (zod); pinned at openapi/v1.json; breaking changes gated by oasdiff (TESTING §6).",
    },
    servers: [
      // API paths live at /v1 directly on the tenant subdomain (spec 16 §3.1;
      // a separate api.* host is explicitly not supported — tenant = subdomain).
      { url: "https://{tenant}.assessly.ai", variables: { tenant: { default: "kazkabel" } } },
    ],
  });
}
